import { useState } from 'react';
import { Check, ChevronDown, Lock, MapPin, ShieldOff, UserPlus, Warehouse, X } from 'lucide-react';
import { PERMISSION_KEYS, ROLE_PERMISSIONS, expandPermissions, roleLabel } from '@supernova/shared';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/empty-state';
import { PasswordInput } from '@/components/ui/password-input';
import { useUsers, useCreateUser, useUpdateUser } from '@/hooks/use-users';
import { useTenantRoles } from '@/hooks/use-tenant-roles';
import { useLocations } from '@/hooks/use-locations';
import { isWarehouseType } from '@/lib/location-type';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import { BUILT_IN_SUMMARY, useUserAccess, type BuiltInRole } from './user-access';
import { UserPermissionsField } from './user-permissions-field';

const inputBase = 'h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

// ── Password Policy Rules (same as signup form) ──
const PASSWORD_RULES = [
  { key: 'minLength', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { key: 'digit', label: 'One numeric digit', test: (p: string) => /\d/.test(p) },
  { key: 'special', label: 'One special character (!@#$%...)', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
] as const;

// Role cards read from least to most access.
const CARD_ORDER = ['employee', 'inventory_manager', 'manager', 'admin'];

const USERS_HASH = '#/users';

const goBack = () => {
  window.location.hash = USERS_HASH;
};

function Section({
  step,
  title,
  description,
  action,
  children,
}: {
  step: number;
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const id = `user-form-section-${step}`;
  return (
    <section aria-labelledby={id} className="relative rounded-xl border border-border bg-white p-5 sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {step}
          </span>
          <div>
            <h2 id={id} className="text-base font-semibold text-foreground">
              {title}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** Add User (no `userId`) and Edit User share this page. */
export function UserFormPage({ userId }: { userId?: string }) {
  const isEdit = userId !== undefined;
  const { user, canCreate, canEdit, canGrant, canActOn, roleOptions } = useUserAccess();

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('employee');
  const [formLocationIds, setFormLocationIds] = useState<string[]>([]);
  const [formExtras, setFormExtras] = useState<string[]>([]);
  const [formCustomRoleId, setFormCustomRoleId] = useState('');
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPermissions, setShowPermissions] = useState(false);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);

  const { data: rolesData } = useTenantRoles({ enabled: canGrant && user?.role !== 'super_admin' });
  const customRoles = rolesData?.data ?? [];
  const selectedCustomRole = customRoles.find((r) => r.id === formCustomRoleId) ?? null;
  const { data: locationsData } = useLocations();
  const locations = locationsData?.data ?? [];

  // All users (active + inactive): the duplicate email check on Add, and the user being edited.
  const { data: allUsersData, isLoading: usersLoading } = useUsers({ page: 1, perPage: 500 });
  const allUsers = allUsersData?.data ?? [];
  const editing = isEdit ? (allUsers.find((u: any) => u.id === userId) ?? null) : null;

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const saving = createUser.isPending || updateUser.isPending;

  // Fill the form once the user being edited has loaded (set during render,
  // React's pattern for state that follows loaded data).
  if (editing && loadedUserId !== editing.id) {
    setLoadedUserId(editing.id);
    setFormName(editing.name || '');
    setFormEmail(editing.email || '');
    setFormPassword('');
    setFormRole(editing.role || 'employee');
    setFormLocationIds(editing.locationIds || []);
    setFormExtras(editing.extraPermissions || []);
    setFormCustomRoleId(editing.customRoleId || '');
    setShowPermissions((editing.extraPermissions || []).length > 0);
  }

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formName.trim()) errors.name = 'Name is required';
    else if (!/^[A-Za-z\s]+$/.test(formName)) errors.name = 'Only alphabet characters and spaces are allowed';
    if (!isEdit) {
      if (!formEmail.trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)) errors.email = 'Please enter a valid email';
      if (!formPassword) errors.password = 'Password is required';
      else if (!PASSWORD_RULES.every((rule) => rule.test(formPassword)))
        errors.password = 'Password does not meet all requirements';
    }
    if (locations.length > 0 && formLocationIds.length === 0) {
      errors.locations = 'Please select at least one location';
    }
    return errors;
  };

  const [inactiveMatch, setInactiveMatch] = useState(false);

  const handleCreate = async () => {
    const existingUser = allUsers.find((u: any) => u.email?.toLowerCase() === formEmail.trim().toLowerCase());
    if (existingUser) {
      if (existingUser.isActive) {
        setFormError('This user already exists.');
      } else {
        setInactiveMatch(true);
        setFormError(
          'This user already exists and is currently inactive. Please reactivate the existing account instead of creating a new one.',
        );
      }
      return;
    }
    try {
      await createUser.mutateAsync({
        email: formEmail,
        name: formName,
        password: formPassword,
        role: formRole,
        locationIds: formLocationIds,
        ...(canGrant ? { extraPermissions: formExtras, customRoleId: formCustomRoleId || null } : {}),
      });
      toast.success('User created');
      goBack();
    } catch (err: any) {
      setFormError(err?.error?.message || 'Failed to create user');
    }
  };

  const handleEdit = async () => {
    try {
      await updateUser.mutateAsync({
        id: userId!,
        name: formName.trim(),
        role: formRole,
        locationIds: formLocationIds,
        ...(canGrant ? { extraPermissions: formExtras, customRoleId: formCustomRoleId || null } : {}),
      });
      toast.success('Changes saved');
      goBack();
    } catch (err: any) {
      setFormError(err?.error?.message || 'Failed to update user');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setInactiveMatch(false);
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (isEdit) await handleEdit();
    else await handleCreate();
  };

  // Custom roles share the role picker with the built-in ones. Their base level
  // goes into formRole, so everything that reads the role keeps working.
  const roleSelectValue = formCustomRoleId ? `custom:${formCustomRoleId}` : formRole;
  // A manager can't load custom roles, so a user's own one is named from the row
  // and its permissions aren't shown.
  const unloadedCustomRole =
    formCustomRoleId && !selectedCustomRole ? (editing?.customRoleName ?? 'Custom role') : null;
  const pickRole = (value: string) => {
    const custom = value.startsWith('custom:') ? customRoles.find((r) => `custom:${r.id}` === value) : undefined;
    const role = custom ? custom.baseRole : value;
    setFormCustomRoleId(custom ? custom.id : '');
    setFormRole(role);
    if (role === 'admin') setFormLocationIds(locations.map((loc: any) => loc.id));
  };

  const toggleLocation = (id: string, on: boolean) => {
    setFormLocationIds((prev) => (on ? [...prev, id] : prev.filter((x) => x !== id)));
    setFieldErrors((prev) => ({ ...prev, locations: '' }));
  };
  const allLocationsPicked = locations.length > 0 && locations.every((l: any) => formLocationIds.includes(l.id));

  // What the summary and the collapsed permissions line report.
  const roleDefaults = expandPermissions(
    selectedCustomRole ? selectedCustomRole.permissions : (ROLE_PERMISSIONS[formRole] ?? []),
  );
  const roleName = unloadedCustomRole ?? (selectedCustomRole ? selectedCustomRole.name : roleLabel(formRole));
  const roleHasEverything = !unloadedCustomRole && PERMISSION_KEYS.every((k) => roleDefaults.includes(k));
  const pickedLocationCount = locations.filter((l: any) => formLocationIds.includes(l.id)).length;

  // ── Access and loading states ──
  const header = (
    <PageHeader
      title={isEdit ? 'Edit User' : 'Add User'}
      description={
        isEdit
          ? 'Change their role, locations and permissions.'
          : 'Create a sign-in for a team member and choose what they can access.'
      }
      backTo={USERS_HASH}
      backLabel="Back to User Management"
    />
  );
  const blocked = (title: string, description: string) => (
    <div className="space-y-4">
      {header}
      <div className="rounded-xl border border-border bg-white">
        <EmptyState
          icon={ShieldOff}
          title={title}
          description={description}
          action={
            <a
              href={USERS_HASH}
              className="inline-flex h-9 items-center rounded-lg border border-border bg-white px-4 text-sm font-medium hover:bg-muted"
            >
              Back to User Management
            </a>
          }
        />
      </div>
    </div>
  );

  if (!isEdit && !(canCreate && roleOptions.length > 0)) {
    return blocked("You can't add users", 'Ask an owner or admin for the Add users permission.');
  }
  if (isEdit) {
    if (usersLoading) {
      return (
        <div className="space-y-4">
          {header}
          <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
        </div>
      );
    }
    if (!editing) return blocked('User not found', 'They may have been removed. Go back to the list and try again.');
    if (!canEdit || !canActOn(editing)) {
      return blocked("You can't edit this user", 'You can only change users ranked below you.');
    }
  }

  const visibleRoleOptions = [...roleOptions].sort((a, b) => CARD_ORDER.indexOf(a.value) - CARD_ORDER.indexOf(b.value));
  const isOwnerRow = formRole === 'owner';

  const roleCard = (value: string, label: string, description: string | null) => {
    const selected = roleSelectValue === value;
    return (
      <label
        key={value}
        className={cn(
          'flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors focus-within:ring-2 focus-within:ring-ring',
          selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-muted/50',
        )}
      >
        <input
          type="radio"
          name="role"
          value={value}
          checked={selected}
          onChange={() => pickRole(value)}
          className="mt-1 h-4 w-4 accent-primary"
        />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-foreground">{label}</span>
          {description && <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>}
        </span>
      </label>
    );
  };

  return (
    <div className="space-y-5">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <a href={USERS_HASH} className="hover:text-foreground hover:underline">
          User Management
        </a>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">{isEdit ? 'Edit User' : 'Add User'}</span>
      </nav>
      {header}

      <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 items-start gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-4 xl:col-span-2">
          {formError && (
            <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
              {inactiveMatch && (
                <a href={`${USERS_HASH}?status=inactive`} className="ml-1 font-semibold underline">
                  Show inactive users
                </a>
              )}
            </div>
          )}

          <Section step={1} title="Profile" description="Their name appears on receipts and reports.">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="user-name" className="mb-1 block text-sm font-medium">
                  Full name
                </label>
                <input
                  id="user-name"
                  type="text"
                  value={formName}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                    setFormName(value);
                    setFieldErrors((prev) => ({ ...prev, name: '' }));
                  }}
                  aria-invalid={!!fieldErrors.name}
                  className={fieldErrors.name ? inputError : inputNormal}
                  placeholder="Jane Doe"
                />
                {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
              </div>
              <div>
                <label htmlFor="user-email" className="mb-1 block text-sm font-medium">
                  Email address
                </label>
                <input
                  id="user-email"
                  type="email"
                  value={formEmail}
                  readOnly={isEdit}
                  onChange={(e) => {
                    setFormEmail(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, email: '' }));
                  }}
                  aria-invalid={!!fieldErrors.email}
                  className={cn(
                    fieldErrors.email ? inputError : inputNormal,
                    isEdit && 'cursor-not-allowed bg-muted text-muted-foreground',
                  )}
                  placeholder="jane@company.com"
                />
                {fieldErrors.email ? (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {isEdit ? "The sign-in email can't be changed." : 'They sign in with this email.'}
                  </p>
                )}
              </div>
            </div>
          </Section>

          {!isEdit && (
            <Section step={2} title="Password" description="Share it with them privately.">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="user-password" className="mb-1 block text-sm font-medium">
                    Password
                  </label>
                  <PasswordInput
                    id="user-password"
                    value={formPassword}
                    onChange={(e) => {
                      setFormPassword(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, password: '' }));
                    }}
                    className={fieldErrors.password ? inputError : inputNormal}
                    placeholder="Enter password"
                  />
                  {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
                </div>
                <ul className="grid grid-cols-1 gap-1.5 self-center sm:pt-5" aria-label="Password rules">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(formPassword);
                    return (
                      <li key={rule.key} className="flex items-center gap-1.5 text-xs">
                        {passed ? (
                          <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        )}
                        <span className={passed ? 'text-green-700' : 'text-muted-foreground'}>
                          {rule.label}
                          <span className="sr-only">{passed ? ' (met)' : ' (not met)'}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Section>
          )}

          <Section
            step={isEdit ? 2 : 3}
            title="Role"
            description="The role decides which screens and buttons they get."
            action={
              canGrant && user?.role !== 'super_admin' ? (
                <a href="#/users/roles" className="text-sm font-medium text-primary hover:underline">
                  Compare roles
                </a>
              ) : undefined
            }
          >
            {isOwnerRow ? (
              <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 p-3.5 text-sm">
                <Lock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span>
                  <span className="font-semibold">Owner</span>
                  <span className="text-muted-foreground"> · The owner role can't be changed.</span>
                </span>
              </div>
            ) : (
              <fieldset>
                <legend className="sr-only">Role</legend>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleRoleOptions.map((opt) =>
                    roleCard(opt.value, opt.label, BUILT_IN_SUMMARY[opt.value as BuiltInRole] ?? null),
                  )}
                </div>
                {(customRoles.length > 0 || unloadedCustomRole) && (
                  <>
                    <p className="mb-2 mt-4 text-xs font-medium text-muted-foreground">Custom roles</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {customRoles.map((role) =>
                        roleCard(`custom:${role.id}`, role.name, `Based on ${roleLabel(role.baseRole)}`),
                      )}
                      {unloadedCustomRole && roleCard(roleSelectValue, unloadedCustomRole, 'Custom role')}
                    </div>
                  </>
                )}
              </fieldset>
            )}
          </Section>

          {locations.length > 0 && (
            <Section
              step={isEdit ? 3 : 4}
              title="Location access"
              description="They only see sales, stock and reports for these locations."
              action={
                <button
                  type="button"
                  onClick={() => {
                    setFormLocationIds(allLocationsPicked ? [] : locations.map((l: any) => l.id));
                    setFieldErrors((prev) => ({ ...prev, locations: '' }));
                  }}
                  className="inline-flex h-8 items-center rounded-lg border border-border bg-white px-3 text-xs font-medium hover:bg-muted"
                >
                  {allLocationsPicked ? 'Clear all' : 'Select all'}
                </button>
              }
            >
              <fieldset>
                <legend className="sr-only">Locations</legend>
                <div
                  className={cn(
                    'grid grid-cols-1 gap-3 sm:grid-cols-2',
                    fieldErrors.locations && 'rounded-lg ring-1 ring-red-500 ring-offset-4',
                  )}
                >
                  {locations.map((loc: any) => {
                    const picked = formLocationIds.includes(loc.id);
                    const warehouse = isWarehouseType(loc.type);
                    const Icon = warehouse ? Warehouse : MapPin;
                    return (
                      <label
                        key={loc.id}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors focus-within:ring-2 focus-within:ring-ring',
                          picked ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-muted/50',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={(e) => toggleLocation(loc.id, e.target.checked)}
                          className="h-4 w-4 accent-primary"
                        />
                        <span className="min-w-0">
                          <span className="block break-words text-sm font-semibold">
                            {loc.nickname ? `${loc.name} (${loc.nickname})` : loc.name}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon className="h-3 w-3" aria-hidden="true" />
                            {warehouse ? 'Warehouse' : 'Store'}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              {fieldErrors.locations && <p className="mt-2 text-xs text-red-600">{fieldErrors.locations}</p>}
            </Section>
          )}

          <Section
            step={(isEdit ? 3 : 4) + (locations.length > 0 ? 1 : 0)}
            title="Permissions"
            description={
              roleHasEverything
                ? `${roleName} includes every permission.`
                : canGrant
                  ? `${roleName} gives ${roleDefaults.length} permissions. Add extras for this person only.`
                  : `${roleName} gives ${roleDefaults.length} permissions.`
            }
            action={
              roleHasEverything || unloadedCustomRole ? undefined : (
                <button
                  type="button"
                  aria-expanded={showPermissions}
                  aria-controls="user-permissions"
                  onClick={() => setShowPermissions((open) => !open)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-white px-3 text-xs font-medium hover:bg-muted"
                >
                  {showPermissions ? 'Hide permissions' : canGrant ? 'Add extra permissions' : 'Show permissions'}
                  <ChevronDown
                    className={cn('h-3.5 w-3.5 transition-transform', showPermissions && 'rotate-180')}
                    aria-hidden="true"
                  />
                </button>
              )
            }
          >
            {unloadedCustomRole ? (
              <div className="rounded-lg border border-input bg-background p-3 text-sm text-muted-foreground">
                {unloadedCustomRole} is a custom role. Owners and admins set its permissions under Roles.
              </div>
            ) : roleHasEverything || showPermissions ? (
              <div id="user-permissions">
                <UserPermissionsField
                  role={formRole}
                  customRole={selectedCustomRole}
                  extras={formExtras}
                  onChange={setFormExtras}
                  canGrant={canGrant}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {formExtras.length > 0
                  ? `${formExtras.length} extra ${formExtras.length === 1 ? 'permission' : 'permissions'} on top of the role.`
                  : 'No extra permissions. Most people only need their role.'}
              </p>
            )}
          </Section>
        </div>

        {/* The summary stays in view beside the form on wide screens, so the
            save buttons live in it; on smaller screens it follows the form. */}
        <aside aria-label="Summary" className="space-y-4 xl:sticky xl:top-0">
          <div className="rounded-xl border border-border bg-white p-5">
            <p className="mb-4 text-xs font-medium text-muted-foreground">Summary</p>
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {formName.trim() ? initials(formName) : <UserPlus className="h-4 w-4" aria-hidden="true" />}
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold">{formName.trim() || 'New user'}</p>
                <p className="truncate text-sm text-muted-foreground">{formEmail.trim() || 'No email yet'}</p>
              </div>
            </div>
            <dl className="text-sm">
              <div className="flex justify-between gap-3 border-t border-border/60 py-2">
                <dt className="text-muted-foreground">Role</dt>
                <dd className="text-right font-medium">{isOwnerRow ? 'Owner' : roleName}</dd>
              </div>
              {locations.length > 0 && (
                <div className="flex justify-between gap-3 border-t border-border/60 py-2">
                  <dt className="text-muted-foreground">Locations</dt>
                  <dd className="font-medium">
                    {pickedLocationCount} of {locations.length}
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-3 border-t border-border/60 py-2">
                <dt className="text-muted-foreground">Extra permissions</dt>
                <dd className="font-medium">{formExtras.length || 'None'}</dd>
              </div>
            </dl>
            <div className="mt-4 flex flex-col gap-2 border-t border-border/60 pt-4 sm:flex-row-reverse xl:flex-col">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-10 items-center sm:flex-1 xl:flex-initial justify-center rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {isEdit ? (saving ? 'Saving...' : 'Save Changes') : saving ? 'Creating...' : 'Create User'}
              </button>
              <a
                href={USERS_HASH}
                className="inline-flex h-10 items-center sm:flex-1 xl:flex-initial justify-center rounded-lg border border-border bg-white px-5 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Cancel
              </a>
            </div>
          </div>
          {!isEdit && (
            <div className="rounded-xl border border-border bg-white p-5 text-sm text-muted-foreground">
              <p className="mb-1 font-semibold text-foreground">Adding someone who left?</p>
              Deactivated users keep their history. Turn them back on from the Inactive tab instead of adding them
              again.
            </div>
          )}
        </aside>
      </form>
    </div>
  );
}
